import React, { Component } from "react";
import { FormField } from "react-form";
import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import MuiTextField from "@material-ui/core/TextField";
import Toolbar from "@material-ui/core/Toolbar";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Close from "@material-ui/icons/Close";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import { FixedSizeGrid } from "react-window";
import { ICON_LIST } from "./icons";

const COLUMN_WIDTH = 80;
const ROW_HEIGHT = 80;

const IconCell = React.memo(({ columnIndex, rowIndex, style, data }) => {
    const { icons, columnCount, value, classes, onSelect, iconColor } = data;

    const index = rowIndex * columnCount + columnIndex;

    if (index >= icons.length) return <div style={style} />;

    const icon = icons[index];
    const coloredIcon = iconColor && icon.component
        ? React.cloneElement(icon.component, { style: { color: iconColor } })
        : icon.component;

    return (
        <div style={{ ...style, padding: 2, boxSizing: "border-box" }}>
            <Tooltip title={icon.id}>
                <div
                    className={`${classes.iconTile}${value === icon.id ? ` ${classes.iconTileSelected}` : ""}`}
                    style={{ width: "100%", height: "100%", boxSizing: "border-box" }}
                    onClick={() => onSelect(icon.id)}
                >
                    <span style={{ fontSize: 24, lineHeight: 1 }}>
                        {coloredIcon || <span style={{ width: 24, height: 24, display: "inline-block" }} />}
                    </span>
                    <div className={classes.iconName}>{icon.id}</div>
                </div>
            </Tooltip>
        </div>
    );
});

const styles = {
    triggerButton: {
        justifyContent: "flex-start",
        textTransform: "none",
        padding: "8px 12px",
        minHeight: 40,
    },
    iconTile: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "10px 4px 6px",
        cursor: "pointer",
        borderRadius: 4,
        border: "2px solid transparent",
        overflow: "hidden",
        "&:hover": {
            backgroundColor: "#e3f2fd",
        },
    },
    iconTileSelected: {
        border: "2px solid #1976d2",
        backgroundColor: "#bbdefb",
        "&:hover": {
            backgroundColor: "#bbdefb",
        },
    },
    iconName: {
        fontSize: 9,
        textAlign: "center",
        wordBreak: "break-word",
        lineHeight: 1.2,
        marginTop: 4,
        color: "rgba(0,0,0,0.6)",
        maxWidth: 68,
        overflow: "hidden",
    },
    resultCount: {
        color: "rgba(0,0,0,0.54)",
        marginBottom: 8,
        display: "block",
    },
};

class IconPickerWrapper extends Component {
    state = {
        open: false,
        search: "",
        gridWidth: 880,
    };

    gridRef = React.createRef();
    gridContainerRef = React.createRef();

    handleOpen = () => this.setState({ open: true });

    handleClose = () => this.setState({ open: false, search: "" });

    handleSelect = (iconId) => {
        const { fieldApi } = this.props;
        fieldApi.setValue(iconId === "Blank" ? null : iconId);
        this.handleClose();
    };

    measureGrid = () => {
        if (this.gridContainerRef.current) {
            this.setState({ gridWidth: this.gridContainerRef.current.offsetWidth });
        }
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.search !== this.state.search && this.gridRef.current) {
            this.gridRef.current.scrollTo({ scrollLeft: 0, scrollTop: 0 });
        }
    }

    render() {
        const { fieldApi, label, classes, iconColor } = this.props;
        const { open, search, gridWidth } = this.state;
        const value = fieldApi.getValue();

        const filtered = search
            ? ICON_LIST.filter(i => i.id.toLowerCase().includes(search.toLowerCase()))
            : ICON_LIST;

        const selectedIcon = value ? ICON_LIST.find(i => i.id === value) : null;
        const columnCount = Math.max(1, Math.floor(gridWidth / COLUMN_WIDTH));
        const rowCount = Math.ceil(filtered.length / columnCount);
        
        const coloredSelectedIcon = iconColor && selectedIcon?.component
            ? React.cloneElement(selectedIcon.component, { style: { color: iconColor } })
            : selectedIcon?.component;

        return (
            <>
                {label && (
                    <Typography
                        style={{ fontSize: 14, color: "rgba(0,0,0,0.54)", display: "block", marginBottom: 4 }}
                    >
                        {label}
                    </Typography>
                )}

                <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        variant="outlined"
                        fullWidth
                        onClick={this.handleOpen}
                        className={classes.triggerButton}
                    >
                        {selectedIcon ? (
                            <>
                                {coloredSelectedIcon}
                                <span style={{ marginLeft: 10, fontSize: 14 }}>{selectedIcon.id}</span>
                            </>
                        ) : (
                            <span style={{ color: "rgba(0,0,0,0.38)", fontSize: 14 }}>Select Icon...</span>
                        )}
                    </Button>
                    {value && (
                        <Tooltip title="Clear icon">
                            <IconButton size="small" style={{ marginLeft: 4 }} onClick={() => fieldApi.setValue(null)}>
                                <Close fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </div>

                <Dialog
                    open={open}
                    fullWidth
                    maxWidth="md"
                    onClose={this.handleClose}
                    TransitionProps={{ onEntered: this.measureGrid }}
                >
                    <Toolbar style={{ backgroundColor: "#d3d3d3" }}>
                        <Typography
                            variant="h6"
                            style={{ flex: 1 }}
                        >
                            Select Icon
                        </Typography>
                        <IconButton onClick={this.handleClose}>
                            <Close />
                        </IconButton>
                    </Toolbar>
                    <DialogContent>
                        <MuiTextField
                            fullWidth
                            placeholder="Search icons..."
                            value={search}
                            onChange={e => this.setState({ search: e.target.value })}
                            style={{ marginBottom: 8 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Tooltip title={<span style={{ fontSize: '1rem' }}>Type a descriptive word to filter icons (e.g. 'water', 'home', 'alert')</span>}>
                                            <InfoOutlined fontSize="small" style={{ color: "rgba(0,0,0,0.38)", cursor: "default" }} />
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                                endAdornment: search ? (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => this.setState({ search: "" })}
                                        >
                                            <Close fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null,
                            }}
                        />
                        <Typography variant="caption" className={classes.resultCount}>
                            {filtered.length} icon{filtered.length !== 1 ? "s" : ""}
                        </Typography>
                        <div ref={this.gridContainerRef}>
                            <FixedSizeGrid
                                ref={this.gridRef}
                                columnCount={columnCount}
                                columnWidth={COLUMN_WIDTH}
                                rowCount={rowCount}
                                rowHeight={ROW_HEIGHT}
                                width={gridWidth}
                                height={420}
                                itemData={{
                                    icons: filtered,
                                    columnCount,
                                    value,
                                    classes,
                                    onSelect: this.handleSelect,
                                    iconColor,
                                }}
                            >
                                {IconCell}
                            </FixedSizeGrid>
                        </div>
                    </DialogContent>
                </Dialog>
            </>
        );
    }
}

IconPickerWrapper.displayName = "IconPickerWrapper";
const IconPickerField = FormField(IconPickerWrapper);
export default withStyles(styles)(IconPickerField);
